import MojitoCore from 'mojito/core';
import { createSelector } from '@reduxjs/toolkit';
import ServicesTypes from 'services/common/types.js';
import { STORE_KEY, initialState } from './slice.js';

const reduxInstance = MojitoCore.Services.redux;
const arraysAreEqual = MojitoCore.Base.objUtils.arraysAreEqual;
const { UNKNOWN } = ServicesTypes.CONTENT_STATE;

/**
 * Tournaments store selectors.
 *
 * @class TournamentsSelectors
 * @name selectors
 * @memberof Mojito.Services.SportsContent.Tournaments
 */

/**
 * Selects tournaments state.
 *
 * @function selectState
 *
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {object} The state of the tournaments store.
 * @memberof Mojito.Services.SportsContent.Tournaments.selectors
 */
export const selectState = state => {
    const appState = state || reduxInstance.store?.getState();
    return appState?.[STORE_KEY] || initialState;
};

/**
 * Selects all tournaments.
 *
 * @function selectTournaments
 *
 * @param {object} [state] - Redux state object.
 * @returns {object} Tournaments map.
 * @memberof Mojito.Services.SportsContent.Tournaments.selectors
 */
export const selectTournaments = state => selectState(state).tournaments;

/**
 * Selects a tournament item.
 *
 * @function selectTournament
 *
 * @param {string} tournamentId - Tournament id.
 * @param {object} [state] - Redux state object.
 * @returns {object|undefined} Tournament content object.
 * @memberof Mojito.Services.SportsContent.Tournaments.selectors
 */
export const selectTournament = (tournamentId, state) => selectTournaments(state)[tournamentId];

/**
 * Selects tournaments by ids.
 *
 * @function selectTournamentsByIds
 *
 * @param {Array<string>} tournamentIds - The ids of wanted tournaments.
 * @param {object} [state] - Redux state object.
 * @returns {Array<object>} List of tournaments.
 * @memberof Mojito.Services.SportsContent.Tournaments.selectors
 */
export const selectTournamentsByIds = (tournamentIds, state) =>
    tournamentIds?.map(id => selectTournaments(state)[id]).filter(Boolean) || [];

/**
 * Selector creator function to fetch tournaments by ids.
 *
 * @function makeSelectTournamentsByIds
 * @returns {Function} Selector function.
 * @memberof Mojito.Services.SportsContent.Tournaments.selectors
 */
export const makeSelectTournamentsByIds = () =>
    createSelector(
        [(tournamentIds, state) => selectTournaments(state), tournamentIds => tournamentIds],
        (tournaments, tournamentIds) =>
            tournamentIds?.map(id => tournaments[id]).filter(Boolean) || [],
        {
            memoizeOptions: {
                resultEqualityCheck: arraysAreEqual,
            },
        }
    );

/**
 * Selects tournament lifetime state in a store.
 *
 * @function selectTournamentState
 *
 * @param {string} tournamentId - Tournament id.
 * @param {object} [state] - Redux state object.
 * @returns {Mojito.Services.Common.types.CONTENT_STATE} Lifetime state of specific tournament.
 * @memberof Mojito.Services.SportsContent.Tournaments.selectors
 */
export const selectTournamentState = (tournamentId, state) => {
    const { tournamentsState } = selectState(state);
    return tournamentsState[tournamentId] || UNKNOWN;
};

const selectTournamentsStateSelector = createSelector(
    [(tournamentIds, state) => selectState(state).tournamentsState, tournamentIds => tournamentIds],
    (tournamentsState, tournamentIds) =>
        tournamentIds?.map(id => tournamentsState[id] || UNKNOWN) || [],
    {
        memoizeOptions: {
            resultEqualityCheck: arraysAreEqual,
        },
    }
);

/**
 * Selects tournaments state.
 *
 * @function selectTournamentsState
 *
 * @param {Array<string>} tournamentIds - The ids of wanted tournament states.
 * @param {object} [state] - Redux state object.
 * @returns {Array<Mojito.Services.Common.types.CONTENT_STATE>} Tournaments state.
 * @memberof Mojito.Services.SportsContent.Tournaments.selectors
 */
export const selectTournamentsState = (tournamentIds, state) =>
    selectTournamentsStateSelector(tournamentIds, state);

/**
 * Subscribes to the changes of particular tournaments state property.
 *
 * @function subscribe
 *
 * @type {Mojito.Core.Services.redux.SubscriberFunction}
 * @memberof Mojito.Services.SportsContent.Tournaments.selectors
 */
export const subscribe = reduxInstance.getSubscriber(STORE_KEY);
